"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useState } from "react";
export const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
export interface UseEmailRecipientsOptions {
  initialRecipients?: string[];
  /** Controlled mode: the committed recipients live with the caller. */
  value?: string[];
  onChange?: (recipients: string[]) => void;
}
export interface UseEmailRecipientsReturn {
  /** Committed recipient emails */
  recipients: string[];
  /** Current text in the input field */
  inputValue: string;
  /** Validation error message, or null if valid */
  error: string | null;
  /** Update the input field value */
  setInputValue: (value: string) => void;
  /** Add an email to the committed list. Returns false if validation fails. */
  add: (email: string) => boolean;
  /** Remove an email from the committed list */
  remove: (email: string) => void;
  /** Pull a committed recipient back into the input for editing */
  edit: (email: string) => void;
  /** Clear all committed recipients */
  clear: () => void;
  /**
   * Returns all committed recipients. If there is pending input that passes
   * validation it is included; invalid pending input is ignored.
   */
  getAll: () => string[];
  /**
   * Keyboard handler for the input — commits on Enter/comma, removes last
   * recipient on Backspace when input is empty. Tab is intentionally left
   * alone so it advances focus to the next field; the blur handler commits
   * any pending value as focus leaves.
   */
  handleInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Blur handler for the input — commits any pending value.
   */
  handleInputBlur: () => void;
}
export function useEmailRecipients(options) {
  const $ = _c(37);
  let t0;
  if ($[0] !== options?.initialRecipients) {
    t0 = options?.initialRecipients ?? [];
    $[0] = options?.initialRecipients;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [internalRecipients, setInternalRecipients] = useState(t0);
  const controlledValue = options?.value;
  const onChange = options?.onChange;
  const recipients = controlledValue ?? internalRecipients;
  let t1;
  if ($[2] !== controlledValue || $[3] !== onChange) {
    t1 = update => {
      if (controlledValue) {
        onChange?.(update(controlledValue));
      } else {
        setInternalRecipients(update);
      }
    };
    $[2] = controlledValue;
    $[3] = onChange;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const setRecipients = t1;
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState(null);
  let t2;
  if ($[5] !== setRecipients) {
    t2 = email => {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed) {
        return false;
      }
      if (!EMAIL_RE.test(trimmed)) {
        setError("Please enter a valid email address");
        return false;
      }
      setError(null);
      setRecipients(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
      setInputValue("");
      return true;
    };
    $[5] = setRecipients;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const add = t2;
  let t3;
  if ($[7] !== setRecipients) {
    t3 = email_0 => {
      setRecipients(prev_0 => prev_0.filter(r => r !== email_0));
    };
    $[7] = setRecipients;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const remove = t3;
  let t4;
  if ($[9] !== setRecipients) {
    t4 = email_1 => {
      setRecipients(prev_1 => prev_1.filter(r_0 => r_0 !== email_1));
      setInputValue(email_1);
      setError(null);
    };
    $[9] = setRecipients;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const edit = t4;
  let t5;
  if ($[11] !== setRecipients) {
    t5 = () => {
      setRecipients(_temp);
      setInputValue("");
    };
    $[11] = setRecipients;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const clear = t5;
  let t6;
  if ($[13] !== error) {
    t6 = value => {
      setInputValue(value);
      if (error) {
        setError(null);
      }
    };
    $[13] = error;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const wrappedSetInputValue = t6;
  let t7;
  if ($[15] !== inputValue || $[16] !== recipients) {
    t7 = () => {
      const all = [...recipients];
      const pending = inputValue.trim().toLowerCase();
      if (pending && EMAIL_RE.test(pending) && !all.includes(pending)) {
        all.push(pending);
      }
      return all;
    };
    $[15] = inputValue;
    $[16] = recipients;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const getAll = t7;
  let t8;
  if ($[18] !== add || $[19] !== inputValue || $[20] !== setRecipients) {
    t8 = e => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        if (inputValue.trim()) {
          add(inputValue);
        }
      } else {
        if (e.key === "Backspace" && !inputValue) {
          setRecipients(_temp2);
        }
      }
    };
    $[18] = add;
    $[19] = inputValue;
    $[20] = setRecipients;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleInputKeyDown = t8;
  let t9;
  if ($[22] !== add || $[23] !== inputValue) {
    t9 = () => {
      if (inputValue.trim()) {
        add(inputValue);
      }
    };
    $[22] = add;
    $[23] = inputValue;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleInputBlur = t9;
  let t10;
  if ($[25] !== add || $[26] !== clear || $[27] !== edit || $[28] !== error || $[29] !== getAll || $[30] !== handleInputBlur || $[31] !== handleInputKeyDown || $[32] !== inputValue || $[33] !== recipients || $[34] !== remove || $[35] !== wrappedSetInputValue) {
    t10 = {
      recipients,
      inputValue,
      error,
      setInputValue: wrappedSetInputValue,
      add,
      remove,
      edit,
      clear,
      getAll,
      handleInputKeyDown,
      handleInputBlur
    };
    $[25] = add;
    $[26] = clear;
    $[27] = edit;
    $[28] = error;
    $[29] = getAll;
    $[30] = handleInputBlur;
    $[31] = handleInputKeyDown;
    $[32] = inputValue;
    $[33] = recipients;
    $[34] = remove;
    $[35] = wrappedSetInputValue;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  return t10;
}
function _temp2(prev_2) {
  return prev_2.slice(0, -1);
}
function _temp() {
  return [];
}